/**
 * Price Display Element.
 *
 */
import { Component, Host, h, Prop, JSX } from '@stencil/core'
import { toMoney, toDecimal } from '@rdss/core'

/**
 * Price display component.
 */
@Component({
    tag: 'wf-price',
    styleUrl: 'wf-price.scss',
    shadow: true,
})
// eslint-disable-next-line require-jsdoc
export class WfPrice {
    /**
     * Price of the article.
     *
     * @type {number}
     */
    @Prop() gross: number

    /**
     * The gross pseudo price.
     *
     * @type {number}
     */
    @Prop() grossPseudo: number

    /**
     * The percent pseudo value.
     *
     * @type {number}
     */
    @Prop() pseudoPercent: number

    /**
     * The size of the package.
     *
     * @type {number}
     */
    @Prop() packageSize: number

    /**
     * The base price of the item.
     *
     * @type {number}
     */
    @Prop() basePrice: number

    /**
     * The base unit.
     *
     * @type {number}
     */
    @Prop() baseUnit: number

    /**
     * The length of the unit.
     *
     * @type {string}
     */
    @Prop() unitLong: string

    /**
     * The short unit label.
     *
     * @type {string}
     */
    @Prop() unitShort: string

    /**
     * Wether or not to use the minimal layout.
     *
     * @type {boolean}
     * @default false
     */
    @Prop() minimalLayout = false

    /**
     * Currency of the price.
     *
     * @type {string}
     * @default 'EUR'
     */
    @Prop() currency = 'EUR'

    /**
     * Locale for formatting.
     *
     * @type {string}
     * @default 'de-DE'
     */
    @Prop() locale = 'de-DE'

    /**
     * Pack unit.
     *
     * @type {string}
     */
    @Prop() packUnit: string

    /**
     * Type of unit label.
     *
     * @type {string}
     * @default 'l'
     */
    @Prop() unitLabelType = 'l'

    /**
     * Label for bottle unit.
     *
     * @type {string}
     * @default 'pro Flasche'
     */
    @Prop() unitLabelBottle = 'pro Flasche'

    /**
     * Label for piece unit.
     *
     * @type {string}
     * @default 'Inhalt:'
     */
    @Prop() unitLabelPiece = 'Inhalt:'

    /**
     * Label for shipping costs.
     *
     * @type {string}
     * @default 'Versandkosten'
     */
    @Prop() labelShippingCosts = 'Versandkosten'

    /**
     * Label for price so far.
     *
     * @type {string}
     * @default 'bisher'
     */
    @Prop() labelPriceSoFar = 'bisher'

    /**
     * Label for price savings.
     *
     * @type {string}
     * @default 'Du sparst'
     */
    @Prop() labelPriceSavings = 'Du sparst'

    /**
     * Info string.
     *
     * @type {string}
     */
    @Prop() info?: string

    /**
     * Info URL.
     *
     * @type {string}
     */
    @Prop() infoUrl?: string

    /**
     * Info URL.
     *
     * @type {boolean}
     */
    @Prop() displayAsterisk = false

    /**
     * Converts number to formatted money string.
     *
     * @param {number} value - The value to format.
     * @returns {string} - The formatted money string.
     */
    private toMoney = (value: number): string => {
        return toMoney(value, this.currency, this.locale)
    }

    /**
     * Converts number to formatted decimal string.
     *
     * @param {number} value - The value to format.
     * @returns {string} - The formatted decimal string.
     */
    private toDecimal = (value: number): string => {
        return toDecimal(value, this.locale)
    }

    /**
     * Checks if pseudo price exists.
     *
     * @returns {boolean} - True if pseudo price exists, otherwise false.
     */
    private hasPseudo = (): boolean => {
        return Boolean(this.grossPseudo && this.pseudoPercent)
    }

    /**
     * Get correct price label for unit.
     *
     * @returns {string} - Display label for unit.
     */
    private get unitLabel(): string {
        return ['ST', 'bundle'].includes(this.unitLabelType) ? this.unitLabelPiece : this.unitLabelBottle
    }

    /**
     * Get formatted gross pseudo price.
     *
     * @returns {string} - Formatted pseudo price string.
     */
    private get formattedGrossPseudoPrice(): string {
        return this.hasPseudo() && `${this.labelPriceSoFar} ${this.toMoney(this.grossPseudo)}`
    }

    /**
     * Get formatted gross price.
     *
     * @returns {string} - Formatted gross price string.
     */
    private get formattedGrossPrice(): string {
        return this.toMoney(this.gross)
    }

    /**
     * Get formatted price details.
     *
     * @returns {string} - Formatted price details string.
     */
    private get priceDetails(): string {
        return [
            `${this.unitLabel} ${this.toDecimal(this.packageSize)} ${this.unitShort}`,
            `(${this.toMoney(this.basePrice)} / ${this.baseUnit} ${this.unitLong})`,
        ].join(' ')
    }

    /**
     * Render the component.
     *
     * @returns {JSX.Element} - The rendered component.
     */
    render(): JSX.Element {
        return (
            <Host>
                <div
                    class={{
                        'gross-price': true,
                        'with-pseudo': this.hasPseudo(),
                    }}
                >
                    {this.minimalLayout ? (
                        <div class="minimal-layout">
                            <div class="pseudo-price">
                                {this.formattedGrossPseudoPrice}{this.hasPseudo() && this.displayAsterisk ? ' *' : ''}
                            </div>
                            <div class="product-price">{this.formattedGrossPrice}{this.displayAsterisk ? ' *' : ''}</div>
                            <div class="reference-unit">{`${this.toDecimal(this.packageSize)} ${this.unitShort}`}&nbsp;
                            ({`${this.toMoney(this.basePrice)} / ${this.baseUnit} ${this.unitLong}`}){this.displayAsterisk ? ' *' : ''}</div>
                        </div>
                    ) : (
                        <div>
                            <div class="pseudo-price">{this.formattedGrossPseudoPrice}</div>
                            <div class="product-price">{this.formattedGrossPrice}</div>
                            <div class="base-price-info detail">
                                <div class="base-price-detail">{this.priceDetails}</div>
                                {this.info && (
                                    <div class="base-price-info">
                                        {this.info} <a href={this.infoUrl}>{this.labelShippingCosts}</a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Host>
        )
    }
}
